import { Controller } from "@hotwired/stimulus"
import { withShortcuts } from "../mixins/shortcuts_mixin"
import { track } from "@amplitude/unified"

class ExpandableController extends Controller {
  static targets = [
    "expandable",
    "collapseTrigger",
    "expandTrigger"
  ]

  static values = {
    ctaLocation: String,
    debug: { type: Boolean, default: false },
    saveToLocalStorage: String,
    turboAutoExpandTarget: String,
    turboAutoExpandAction: { type: String, default: "prepend" }
  }

  connect() {
    this.set(this.isOpenDefault())
    this.#notifyManager()
  }

  isOpenDefault() {
    let openDefault = this.hasExpandableTarget ? this.expandableTarget.dataset.open : "false"

    if (this.hasSaveToLocalStorageValue) {
      let setting = localStorage.getItem(this.saveToLocalStorageValue) || openDefault
      return setting === "true"
    }

    return openDefault === "true"
  }

  toggle(event) {
    const newState = !this.isOpen
    const source = this.#toggleSource(event)

    this.log("Toggle called", {
      newState,
      source,
      eventType: event?.type,
      hasCtaLocation: this.hasCtaLocationValue
    })

    this.#track(newState, source)
    this.set(newState)
    this.#notifyManager()
  }

  registerManager(manager) {
    this.manager = manager
  }

  unregisterManager() {
    this.manager = undefined
  }

  set(isOpen) {
    this.isOpen = isOpen
    this.#adjustUI()
  }

  #toggleSource(event) {
    if (event?.type === "click") return "button_click"
    if (event?.type?.startsWith("shortcut:")) return "keyboard_shortcut"
    return "unknown"
  }

  #track(newState, source) {
    if (!this.hasCtaLocationValue) return

    const trackingData = {
      toggled_to: newState ? "open" : "closed",
      source: source,
      cta_location: this.ctaLocationValue
    }

    this.log("Tracking Panel Toggled", trackingData)
    track("Panel Toggled", trackingData)
  }

  #notifyManager() {
    if (!this.manager) return

    this.manager.childUpdated()
  }

  #adjustUI() {
    this.expandableTargets.forEach((item) => {
      item.classList.toggle("hidden", !this.isOpen)
    })

    if (this.hasCollapseTriggerTarget) {
      this.collapseTriggerTarget.classList.toggle("hidden", !this.isOpen)
    }

    if (this.hasExpandTriggerTarget) {
      this.expandTriggerTarget.classList.toggle("hidden", this.isOpen)
    }

    if (this.hasSaveToLocalStorageValue) {
      localStorage.setItem(this.saveToLocalStorageValue, this.isOpen.toString())
    }
  }

  onShortcut(event) {
    this.log("Shortcut triggered", { eventType: event?.type })
    this.toggle(event)
  }

  handleTurboStreamRender(event) {
    const { target, action } = event.detail?.newStream || {}

    if (this.hasTurboAutoExpandTargetValue && action === this.turboAutoExpandActionValue && target?.includes(this.turboAutoExpandTargetValue)) {
      this.log("Turbo Stream auto-expand triggered", { target, action })
      this.set(true)
    }
  }

  log(message, data = null) {
    if (this.debugValue) {
      console.log(`[ExpandableController] ${message}`, data || "")
    }
  }

}

export default withShortcuts(ExpandableController)
